import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from "@angular/core";
import { Router } from "@angular/router";
import {
  engagementRestoreEngagementFromRemoteStorage,
  engagementRestoreFromArchiveEngagement,
  engagementIsRollForwardEngagement,
  engagementGetWarningsAndConflictsCountByEngagementId,
  engagementDeleteEngagement,
} from "backend/api/engagement.service";
import { teamIsUserLastActiveTeamMember } from "backend/api/team.service";
import { AccessRole } from "backend/model/accessRole";
import { EngagementOption } from "backend/model/engagementOption";
import { EngagementRole } from "backend/model/engagementRole";
import { EngagementState } from "backend/model/engagementState";
import { EngagementTeam } from "backend/model/engagementTeam";
import { EngagementTeamMember } from "backend/model/engagementTeamMember";
import { EngagementTitle } from "backend/model/engagementTitle";
import { IEngagementTitle } from "backend/model/iEngagementTitle";
import { TeamMemberStatus } from "backend/model/teamMemberStatus";
import dayjs from "dayjs";
import { DynamicDialogRef } from "primeng/dynamicdialog";
import { Subject } from "rxjs";
import { ICopyCloneAndRollforwardModalProps } from "src/app/workspace/workspace-list/workspace-list-tree/workspace-list-tree.component";
import {
  FavoritesLabel,
  IWorkspaceRowData,
  WorkspaceTreeNode,
} from "src/app/workspace/workspace-list/workspace-list.component";
import { FORMAT_EMPTY } from "src/config";
import { type StandardConfirmationDialogRequest } from "src/standard/standard-confirmation-dialog/standard-confirmation-dialog.types";
import { ModalPage } from "src/standard/standard-modal-button/ModalPage";
import { ModalDialogService } from "src/standard/standard-modal-button/showModalDialog";
import {
  ShowWindowDialogOptions,
  WindowDialogService,
} from "src/standard/standard-modal-button/showWindowDialog";
import { WritableContextSignals } from "src/standard/standard-modal-button/types";

import { formatDate } from "src/standard/ts/formatDate";
import { IPageContext } from "src/standard/ts/pageContextUtils";
import invariant from "tiny-invariant";
import { HttpClient } from "@angular/common/http";
import { FavoriteEngagementWatcherService } from "src/app/workspace/workspace-list/favorite-engagement-watcher.service";
import { EngagementService } from "src/app/services/engagement.service";
import { StandardLogService } from "src/standard/logging/logging";
import { TEAM_MANAGEMENT_PATH } from "src/app/workspace/routes";
import {
  DateRangeType_DateTimeCode,
  DateRangeType_DayCode,
  DateRangeType_TimeCode,
} from "src/standard/ts/standardTypes";
import { APTContext } from "src/app/services/aptContext/apt-context";
import { precacheComponent } from "src/standard/ts/prerun/precacheComponent";
import {
  createObservableBroadcastChannel,
  ObservableBroadcastChannel,
} from "src/app/services/aptContext/broadcast-channels";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { parseAptUrl } from "src/standard/ts/parseAptUrl";
import { considerUtcTimeAsLocalTime } from "src/standard/ts/timeUtils";
import { RestoreEngagementFromRemoteStorageCommandResult } from "backend/model/restoreEngagementFromRemoteStorageCommandResult";
import { CommandResult } from "backend/model/commandResult";
import { getEffectiveAptUrl } from "src/app/guards/getEffectiveAptUrl";
import { RemoteStorageStates } from "src/app/services/RemoteStorageStates";
import { WorkspaceListNavigationService } from "src/app/services/workspace-list-navigation.service";

const LIST_ITEM_STATES_KEY = "ListItemStates";
export const PROBLEM_DETECTED = $localize`Problem detected`;
const INFORMATION = $localize`Information`;
const SUCCESS = $localize`Success`;
const ERROR = $localize`Error`;

/** Used when user is atempting to open a remote storage engagement */
export const REMOTE_STORAGE_WARNING = $localize`Engagement can only be opened once it has been restored.`;
/** Used when user is attempting to perform an action on a remote storage engagement */
export const STALE_REMOTE_STORAGE_WARNING = $localize`Unable to perform this action as engagement has been moved to remote storage.`;
export const MARKED_FOR_DELETION_WARNING = $localize`You have been deactivated from this workspace. If you still require access, contact a team admin. This workspace will close when you click OK.`;
export const REMOVED_WORKSPACE_WARNING = $localize`Restore this workspace before trying again to open it`;
export const CROSSBORDER_BLOCKLIST_WARNING = $localize`Access to this engagement is currently restricted. Speak to the Group audit engagement partner for more information.`;

const COPY_CLONE_CONFLICTS_AND_WARNINGS_MESSAGE = $localize`This workspace contains uncleared warnings and unresolved conflicts. It is strongly recommended that you clear/resolve them before copying the workspace. To clear/resolve them now, select cancel and open the workspace. Continue?`;
const COPY_CLONE_CONFLICTS_MESSAGE = $localize`This workspace contains unresolved conflicts. It is strongly recommended that you resolve them before copying the workspace. To resolve them now, select cancel and open the workspace. Continue?`;
const COPY_CLONE_WARNINGS_MESSAGE = $localize`This workspace contains uncleared warnings. It is strongly recommended that you clear them before copying the workspace. To clear them now, select cancel and open the workspace. Continue?`;

const ROLLFORWARD_CONFLICTS_AND_WARNINGS_MESSAGES = [
  $localize`This workspace contains uncleared warnings and unresolved conflicts. It is strongly recommended that you resolve these issues before copying the workspace.`,
  $localize`Components that do not have any relationships will be removed from the Group workspace. To do this now, select Cancel and open the workspace. Continue?`,
];
const ROLLFORWARD_CONFLICTS_MESSAGES = [
  $localize`This workspace contains unresolved conflicts. It is strongly recommended that you resolve these before copying the workspace.`,
  $localize`Components that do not have any relationships will be removed from the Group workspace. To resolve them now, select Cancel and open the workspace. Continue?`,
];
const ROLLFORWARD_WARNINGS_MESSAGES = [
  $localize`This workspace contains uncleared warnings. It is strongly recommended that you clear them before copying the workspace.`,
  $localize`Components that do not have any relationships will be removed from the Group workspace. To clear them now, select Cancel and open the workspace. Continue?`,
];

@Injectable({
  providedIn: "root",
})
export class WorkspaceListService {
  private readonly workspaceListNavigationService = inject(
    WorkspaceListNavigationService,
  );
  private readonly destroyRef = inject(DestroyRef);

  listItemStates: { [key: string]: boolean } = {
    [FavoritesLabel]: true,
  };

  isWorkspaceListOpen = signal<boolean>(false);
  isWorkSpacePropertiesOpen = signal<boolean>(false);
  globalSideNavEl = signal<HTMLElement | undefined>(undefined);
  workspaceListModalConfig = computed(() => {
    return {
      canChangeSnap: false,
      canClose: true,
      canDrag: false,
      canMaximize: false,
      canMinimize: false,
      canOpenInNewWindow: false,
      canResizeX: "end",
      canResizeY: false,
      modal: true,
      snapDirection: "left",
      snapSize: "555px",
      heightVariant: "withinShellHeader",
      maxWidth: "1080px",
    } as const satisfies ShowWindowDialogOptions;
  });

  modalPage:
    | (ModalPage<{}> & WritableContextSignals<IPageContext<{}>>)
    | undefined;

  private closeNavSideSource = new Subject<boolean>();
  closeNavSide$ = this.closeNavSideSource.asObservable();

  public emitCloseNavSide(value: boolean): void {
    this.closeNavSideSource.next(value);
  }

  public triggerNavSideClose(): void {
    this.closeNavSideSource.next(true);
  }

  private removedEngagementsBC: ObservableBroadcastChannel<string[]>;

  private router = inject(Router);
  private httpClient = inject(HttpClient);
  private aptContext = inject(APTContext);
  private engagementService = inject(EngagementService);
  private modalDialogService = inject(ModalDialogService);
  private standardLogService = inject(StandardLogService);
  private windowDialogService = inject(WindowDialogService);
  private favoriteEngagementWatcher = inject(FavoriteEngagementWatcherService);

  public readonly isViewingWorkspaceProperties = signal<boolean>(false);
  public readonly isViewingCloneCopyRollforward = signal<boolean>(false);
  childDialogRefs = signal<DynamicDialogRef<unknown>[]>([]);
  selectedEngagementId = signal<string>("");
  selectedEngagementMemberFirmId = signal<number>(0);
  engagementDetailsRef: DynamicDialogRef | undefined;
  private workspaceListDialogRef: DynamicDialogRef<unknown> | undefined;

  constructor() {
    this.removedEngagementsBC = createObservableBroadcastChannel(
      "removed-engagements",
    );

    this.removedEngagementsBC.obs$
      .pipe(takeUntilDestroyed())
      .subscribe(async (message) => {
        await this.closeRemovedWorkspace(message);
      });

    this.openWorkspaceList = this.openWorkspaceList.bind(this);
  }

  isRemoteStorageState(state: string): boolean {
    return RemoteStorageStates.includes(state);
  }

  /**
   * @returns true when the engagement state has an error that prevents navigation
   */
  async getEngagementStateNavError(state: string): Promise<boolean> {
    switch (state) {
      case EngagementState.MarkedForDeletion:
        await this.showInformationModal(
          PROBLEM_DETECTED,
          MARKED_FOR_DELETION_WARNING,
        );
        return true;

      case EngagementState.RemoteStorage:
      case EngagementState.InTransit:
      case EngagementState.InTransitFailed:
      case EngagementState.Restoring:
      case EngagementState.RestoreFailed:
      case EngagementState.DeletionPending:
      case EngagementState.EngagementDeletionFailed:
        await this.showInformationModal(
          PROBLEM_DETECTED,
          REMOTE_STORAGE_WARNING,
        );
        return true;

      default:
        return false;
    }
  }

  isGroupAdminRole(role: EngagementRole) {
    const groupAdminRoles = [
      EngagementRole.Preparer,
      EngagementRole.DetailedReviewer,
      EngagementRole.OtherReviewer,
      EngagementRole.EngagementPartner,
      EngagementRole.Eqcr,
    ];

    return groupAdminRoles.includes(role);
  }

  setListItemState(label: string, expanded: boolean): void {
    const listItemStates = this.getListItemStates();
    listItemStates[label] = expanded;
    const statesString = JSON.stringify(listItemStates);
    localStorage.setItem(LIST_ITEM_STATES_KEY, statesString);
  }

  setListItemStates(listItemStates: { [key: string]: boolean }) {
    const statesString = JSON.stringify(listItemStates);
    localStorage.setItem(LIST_ITEM_STATES_KEY, statesString);
  }

  getListItemState(label: string): boolean {
    const listItemStates = this.getListItemStates();
    return listItemStates[label] ?? true;
  }

  getListItemStates(): { [key: string]: boolean } {
    const statesString = localStorage.getItem(LIST_ITEM_STATES_KEY);
    return statesString
      ? (JSON.parse(statesString) as { [key: string]: boolean })
      : {
          [FavoritesLabel]: true,
        };
  }

  closeNavSide() {
    this.closeNavSideSource.next(true);
  }

  async closeWorkspaceList() {
    if (this.modalPage) {
      await this.modalPage.close();
      this.isWorkspaceListOpen.set(false);
    }
  }

  formatArchiveDeadline(
    item: IWorkspaceRowData | EngagementTitle | IEngagementTitle,
  ): string | undefined {
    if (
      (item.state !== EngagementState.EngagementSignedOff &&
        item.state !== EngagementState.PlanningSignedOff) ||
      item.reportDate === undefined ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      item.reportDate === null ||
      item.housekeepingPeriod === undefined ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      item.housekeepingPeriod === null
    ) {
      return undefined;
    }

    const archiveDeadlineDate = dayjs(item.reportDate).add(
      item.housekeepingPeriod,
      "day",
    );

    const archiveDeadlineDateString = formatDate(archiveDeadlineDate);

    let compareDate = dayjs();
    if (this.aptContext.isOfflineWebMode && item.copiedAt) {
      compareDate = dayjs(item.copiedAt);
    }

    const diffDays = archiveDeadlineDate.diff(compareDate, "day");
    const diffHours = archiveDeadlineDate.diff(compareDate, "hour") % 24;
    const diffMinutes = archiveDeadlineDate.diff(compareDate, "minute") % 60;

    // Only exists for case of same day but past deadline
    let diffDaysPrefix = "";
    let diffhoursPrefix = "";
    if (compareDate.isAfter(archiveDeadlineDate) && diffDays === 0) {
      diffDaysPrefix = "-";
    }

    const timeleft =
      (Math.abs(diffHours) < 10
        ? "0" + Math.abs(diffHours)
        : Math.abs(diffHours)) +
      ":" +
      (Math.abs(diffMinutes) < 10
        ? "0" + Math.abs(diffMinutes)
        : Math.abs(diffMinutes));
    // Format the output string
    if (diffDays === 0) {
      diffDaysPrefix = "";
      diffhoursPrefix = "-";
    }
    return $localize` - ${archiveDeadlineDateString} (${diffDaysPrefix}${diffDays} days and ${diffhoursPrefix}${timeleft} hours)`;
  }

  createWorkspaceTreeNode(
    item: IWorkspaceRowData | EngagementTitle | IEngagementTitle,
  ): WorkspaceTreeNode {
    const startDateStr = formatDate(
      considerUtcTimeAsLocalTime(item.startDate),
      DateRangeType_DayCode,
      FORMAT_EMPTY,
      false,
    );
    const endDateStr = formatDate(
      considerUtcTimeAsLocalTime(item.endDate),
      DateRangeType_DayCode,
      FORMAT_EMPTY,
      false,
    );
    const copiedAtStr = formatDate(
      item.copiedAt,
      DateRangeType_DateTimeCode,
      FORMAT_EMPTY,
      false,
    );
    const copiedAt = copiedAtStr
      ? $localize`Snapshot copied at: ${copiedAtStr}`
      : undefined;
    invariant(item.clientId && item.engagementId);
    let subLabel = $localize`${startDateStr} - ${endDateStr}`;

    const isArchived = computed(() => {
      const state = item.state;
      return (
        state === EngagementState.Archived ||
        state === EngagementState.Archiving ||
        state === EngagementState.RemoteStorage ||
        state === EngagementState.Restoring ||
        state === EngagementState.RestoreFailed ||
        state === EngagementState.EngagementDeletionFailed ||
        state === EngagementState.DeletionPending ||
        state === EngagementState.InTransit ||
        state === EngagementState.InTransitFailed
      );
    });

    if (isArchived() && item.archivedDate) {
      const archivedDateStr = formatDate(item.archivedDate);
      subLabel += $localize` - Archived ${archivedDateStr}`;
    }

    let lockedTooltip: string | undefined = undefined;
    if (item.state === EngagementState.Locked) {
      const lockedDate = formatDate(item.lockedOn);
      const lockedTime = formatDate(
        item.lockedOn,
        DateRangeType_TimeCode,
        FORMAT_EMPTY,
      );

      lockedTooltip = $localize`Locked on ${lockedDate} at ${lockedTime}`;
    }
    const offlineText = this.aptContext.isOfflineWebMode
      ? $localize` - Offline`
      : undefined;

    return {
      label: item.engagementName ?? FORMAT_EMPTY,
      data: {
        clientId: item.clientId,
        engagementId: item.engagementId,
        subLabel,
        purpose: item.engagementPurpose,
        state: item.state,
        status: item.status,
        role: item.teamMemberEngagementRole,
        unopened: item.status === TeamMemberStatus.Invited,
        archiveDeadline: this.formatArchiveDeadline(item),
        endDate: endDateStr,
        memberFirmId: item.memberFirmId,
        libraryVersion: item.libraryVersion,
        lockedTooltip,
        isCrossBorderBlockListEngagement: item.isCrossBorderBlockListEngagement,
        copiedAt: copiedAt,
        offlineText,
      },
    };
  }

  async navToWorkspace(
    node: WorkspaceTreeNode,
    loading: WritableSignal<boolean>,
    modalPage: ModalPage<{}> & WritableContextSignals<IPageContext<{}>>,
    isSupportUser: boolean,
  ) {
    loading.set(true);

    invariant(node.data.engagementId);
    invariant(node.data.state);
    invariant(node.data.status);
    invariant(node.data.memberFirmId);

    const engId = node.data.engagementId;
    const mfId = node.data.memberFirmId;

    try {
      this.aptContext.setSupportUserSelectedMemberFirmId(`${mfId}`);
      const engagementState = (await this.getEngagementTitle(engId, mfId))
        .state;
      this.aptContext.resetSupportUserSelectedMemberFirmId();

      const stateNavError =
        await this.getEngagementStateNavError(engagementState);
      if (stateNavError) {
        loading.set(false);
        return;
      }

      if (node.data.isCrossBorderBlockListEngagement) {
        await this.showInformationModal(
          PROBLEM_DETECTED,
          CROSSBORDER_BLOCKLIST_WARNING,
        );
        loading.set(false);
        return;
      }

      if (
        !isSupportUser &&
        node.data.status !== TeamMemberStatus.Active &&
        node.data.status !== TeamMemberStatus.Invited
      ) {
        await this.showInformationModal(
          PROBLEM_DETECTED,
          REMOVED_WORKSPACE_WARNING,
        );
        loading.set(false);
        return;
      }

      if (!isSupportUser) {
        void this.workspaceListNavigationService.updateTeamMemberStatus(
          node.data.engagementId,
          EngagementState.Active,
          node.data.memberFirmId,
        );
      }

      const [url] = getEffectiveAptUrl(this.router.routerState.snapshot.url);
      const aptUrlInfo = parseAptUrl(url);

      let newUrlRoute = `/ws/${node.data.engagementId}`;

      if (isSupportUser) {
        newUrlRoute += `/${TEAM_MANAGEMENT_PATH}`;
      }

      const currentEngagementId = aptUrlInfo.engagementId;

      if (aptUrlInfo.isWithinEngagement && currentEngagementId) {
        if (currentEngagementId !== node.data.engagementId) {
          const newUrl = getEffectiveAptUrl(newUrlRoute)[0];
          window.open(newUrl, "_blank");
        }
        await modalPage.close();
      } else {
        this.workspaceListNavigationService.lastEngagementIdNavigatedTo =
          node.data.engagementId;
        this.aptContext.setSupportUserSelectedMemberFirmId(`${mfId}`);
        this.aptContext.saveSelectedMemberFirmId(`${mfId}`);
        await this.router.navigateByUrl(newUrlRoute);
      }
      loading.set(false);

      this.isWorkspaceListOpen.set(false);
    } catch (error) {
      loading.set(false);
      return;
    }
  }

  async removeWorkspace(engagements: WorkspaceTreeNode[]): Promise<boolean> {
    let showLastTeamMemberWarning = false;
    const lastTeamMemberEngagements: string[] = [];

    let showLastAdminWarning = false;
    const lastAdminEngagements: string[] = [];

    let showInTransitWarning = false;
    const inTransitEngagements: string[] = [];

    let removeFailed = false;
    const removedFailedEngagements: string[] = [];

    let showSuccessNotification = false;
    const removedEngagementIds: string[] = [];

    for (const node of engagements) {
      invariant(node.label);
      invariant(node.data.state);
      invariant(node.data.status);
      invariant(node.data.memberFirmId);
      invariant(node.data.engagementId);

      if (this.isRemoteStorageState(node.data.state)) {
        showInTransitWarning = true;
        inTransitEngagements.push(node.label);
        continue;
      }

      const isUserLastActiveTeamMember = await this.isUserLastActiveTeamMember(
        node.data.engagementId,
      );

      const teamMember = await this.getWhoAmI(
        node.data.engagementId,
        node.data.memberFirmId,
      );
      const engagementTeam = await this.getEngagementTeam(
        node.data.engagementId,
      );

      let engagementHasOtherAdmin = false;
      engagementTeam.members?.forEach((member) => {
        if (
          member.teamMemberId !== teamMember.teamMemberId &&
          member.accessRole === AccessRole.TeamAdmin &&
          member.status === TeamMemberStatus.Active
        ) {
          engagementHasOtherAdmin = true;
        }
      });

      if (
        !isUserLastActiveTeamMember &&
        teamMember.accessRole === AccessRole.TeamAdmin &&
        !engagementHasOtherAdmin &&
        engagementTeam.members &&
        engagementTeam.members.length > 1
      ) {
        showLastAdminWarning = true;
        lastAdminEngagements.push(node.label);
        continue;
      }

      const removedStatus =
        node.data.status === TeamMemberStatus.Invited ||
        node.data.status === TeamMemberStatus.InvitedInactive
          ? TeamMemberStatus.InvitedInactive
          : TeamMemberStatus.Inactive;

      const removeResult =
        await this.workspaceListNavigationService.updateTeamMemberStatus(
          node.data.engagementId,
          removedStatus,
          node.data.memberFirmId,
        );

      if (removeResult) {
        if (removeResult === "Team_0021") {
          showLastAdminWarning = true;
          lastAdminEngagements.push(node.label);
          continue;
        } else {
          removeFailed = true;
          removedFailedEngagements.push(node.label);
          continue;
        }
      }

      if (
        this.favoriteEngagementWatcher
          .favoriteEngagementIds()
          .includes(node.data.engagementId)
      ) {
        this.favoriteEngagementWatcher.removeFavoriteEngagement(
          node.data.engagementId,
        );
      }

      if (isUserLastActiveTeamMember) {
        showLastTeamMemberWarning = true;
        lastTeamMemberEngagements.push(node.label);
        continue;
      }

      showSuccessNotification = true;
      removedEngagementIds.push(node.data.engagementId);
    }

    if (removeFailed) {
      const message = $localize`An unknown error occurred. The following workspace(s) were not removed.`;
      await this.showInformationModal(
        INFORMATION,
        message,
        removedFailedEngagements,
      );
      return false;
    }

    if (showInTransitWarning) {
      const message = $localize`Unable to perform this action as the following engagement(s) have been moved to remote storage.`;
      void this.showInformationModal(
        PROBLEM_DETECTED,
        message,
        inTransitEngagements,
      );
      return false;
    }

    if (showLastAdminWarning) {
      const message = $localize`You are the last active team admin in the following workspace(s). You need to make at least one other team member a team admin before you are able to remove it.`;
      void this.showInformationModal(
        INFORMATION,
        message,
        lastAdminEngagements,
      );
      return false;
    }

    if (showLastTeamMemberWarning) {
      const message = $localize`You are the last active team member in the following workspace(s). To access the workspace again, select the 'removed' filter in the workspace list and restore the workspace.`;
      void this.showInformationModal(
        INFORMATION,
        message,
        lastTeamMemberEngagements,
      );
    }

    if (showSuccessNotification) {
      void this.showInformationModal(
        INFORMATION,
        $localize`This workspace has been removed. If you need access to this workspace again, restore the workspace.`,
      );
    }

    this.removedEngagementsBC.postMessage(removedEngagementIds);
    await this.closeRemovedWorkspace(removedEngagementIds);

    return true;
  }

  async closeRemovedWorkspace(removedEngagementIds: string[]) {
    const url = this.router.routerState.snapshot.url;
    const urlParts = url.split("/");

    if (url.includes("ws") && urlParts.length > 2) {
      const currentEngagementId = urlParts[2];
      if (
        currentEngagementId &&
        removedEngagementIds.includes(currentEngagementId)
      ) {
        this.engagementService.setEngagementDetails(null);
        await this.router.navigateByUrl("/ws");
      }
    }
  }

  async restoreWorkspace(engagements: WorkspaceTreeNode[]): Promise<boolean> {
    let restoreFailed = false;
    const restoreFailedEngagements: string[] = [];

    for (const node of engagements) {
      invariant(node.label);
      invariant(node.data.status);
      invariant(node.data.memberFirmId);
      invariant(node.data.engagementId);

      const newStatus =
        node.data.status === TeamMemberStatus.InvitedInactive
          ? TeamMemberStatus.Invited
          : TeamMemberStatus.Active;

      const restoreResult =
        await this.workspaceListNavigationService.updateTeamMemberStatus(
          node.data.engagementId,
          newStatus,
          node.data.memberFirmId,
        );

      if (restoreResult) {
        restoreFailed = true;
        restoreFailedEngagements.push(node.label);
      }
    }

    if (restoreFailed) {
      const message = $localize`An unknown error occurred. The following workspace(s) were not restored.`;
      await this.showInformationModal(
        INFORMATION,
        message,
        restoreFailedEngagements,
      );
      return false;
    }

    return true;
  }

  async restoreWorkspaceFromRemote(node: WorkspaceTreeNode): Promise<boolean> {
    invariant(node.label);
    invariant(node.data.state);
    invariant(node.data.engagementId);

    if (this.isRemoteStorageState(node.data.state)) {
      const modalConfig = {
        title: $localize`Warning`,
        message: $localize`Restore this engagement?`,
        confirmText: $localize`OK`,
        cancelText: $localize`Cancel`,
        variant: "confirm",
      } satisfies StandardConfirmationDialogRequest;

      const result =
        await this.modalDialogService.showConfirmationDialog(modalConfig);

      if (result) {
        try {
          const restoreFromRemoteResult =
            await this.restoreEngagementFromRemoteStorage(
              node.data.engagementId,
            );

          if (
            restoreFromRemoteResult.isAlreadyRestoring &&
            !restoreFromRemoteResult.isServiceBusMessageFailed
          ) {
            await this.showInformationModal(
              INFORMATION,
              $localize`This engagement is in the process of being restored.`,
            );
            return true;
          } else if (
            !restoreFromRemoteResult.isAlreadyRestoring &&
            !restoreFromRemoteResult.isServiceBusMessageFailed &&
            restoreFromRemoteResult.isEngagementStatusUpdated
          ) {
            await this.showInformationModal(
              SUCCESS,
              $localize`Restore process has started.`,
            );
            return true;
          } else {
            await this.showInformationModal(
              ERROR,
              $localize`Engagement restore failed. Contact support for assistance.`,
            );
            return false;
          }
        } catch (exception) {
          this.standardLogService.logException(exception);

          await this.showInformationModal(
            INFORMATION,
            $localize`An unknown error occurred. The workspace was not restored.`,
          );
        }
      }
    }

    return false;
  }

  async unarchiveWorkspace(
    engagementId: string,
    engagementMemberFirmId: number | undefined,
  ) {
    const engagementTitle = await this.getEngagementTitle(
      engagementId,
      engagementMemberFirmId,
    );

    if (
      engagementTitle.state === EngagementState.Archived ||
      engagementTitle.state === EngagementState.Archiving
    ) {
      const confirmResult =
        await this.modalDialogService.showConfirmationDialog({
          title: $localize`Information`,
          message: $localize`Continue unarchiving this engagement?`,
          confirmText: $localize`Yes`,
          cancelText: $localize`Cancel`,
        });

      let restoreResult: CommandResult | undefined = undefined;

      if (confirmResult) {
        restoreResult = await this.restoreEngagementFromArchive(engagementId);
      }

      return restoreResult;
    }

    return undefined;
  }

  async showCopyCloneOrRollForwardWorkspace(
    engagementOption: EngagementOption,
    engagementId: string,
    fromSearchPanel: boolean,
    inlineStartSnapElement: HTMLElement | undefined,
    childDialogRefs: WritableSignal<DynamicDialogRef<unknown>[]>,
    engagementMemberFirmId: number,
  ) {
    if (engagementOption !== EngagementOption.Edit) {
      const canContinue = await this.checkForWarningsAndConflicts(
        engagementId,
        engagementOption,
        engagementMemberFirmId,
      );

      if (!canContinue) {
        return false;
      }
    }

    const [response, dialogRef] = await this.windowDialogService.showWithRef(
      this.getWorkspaceCopyCloneAndRollForwardContent,
      {
        engagementId,
        engagementOption,
        fromSearchPanel,
        engagementMemberFirmId,
      } as ICopyCloneAndRollforwardModalProps,
      {
        canChangeSnap: false,
        canClose: true,
        canDrag: false,
        canMaximize: false,
        canMinimize: false,
        canOpenInNewWindow: false,
        canResizeX: "end",
        canResizeY: false,
        modal: true,
        snapDirection: "left",
        snapSize: "420px",
        ignoreExistingSnappedWindows: false,
        inlineStartSnapElement: undefined,
        heightVariant: "withinShellHeader",
      },
      this.engagementDetailsRef,
      childDialogRefs,
    );

    if (engagementOption === EngagementOption.Edit) {
      this.isViewingWorkspaceProperties.set(true);
    }

    if (
      engagementOption === EngagementOption.Copy ||
      engagementOption === EngagementOption.Clone ||
      engagementOption === EngagementOption.RollForward ||
      engagementOption === EngagementOption.Create
    ) {
      this.isViewingCloneCopyRollforward.set(true);
    }

    dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.engagementDetailsRef = undefined;
        this.isWorkSpacePropertiesOpen.set(false);

        if (engagementOption === EngagementOption.Edit) {
          this.isViewingWorkspaceProperties.set(false);
        }
        if (
          engagementOption === EngagementOption.Copy ||
          engagementOption === EngagementOption.Clone ||
          engagementOption === EngagementOption.RollForward ||
          engagementOption === EngagementOption.Create
        ) {
          this.isViewingCloneCopyRollforward.set(false);
        }
      });

    this.engagementDetailsRef = dialogRef;
    this.isWorkSpacePropertiesOpen.set(true);
    const result = await response;
    return result;
  }

  async showInformationModal(
    title: string,
    message: string,
    bullets?: string[],
  ) {
    const modalConfig = {
      title,
      message,
      bullets,
      variant: "informational",
    } satisfies StandardConfirmationDialogRequest;

    await this.modalDialogService.showConfirmationDialog(modalConfig);
  }

  private async restoreEngagementFromRemoteStorage(
    engagementId: string,
  ): Promise<RestoreEngagementFromRemoteStorageCommandResult> {
    return await this.aptContext.getAPTResultOrThrow(
      await engagementRestoreEngagementFromRemoteStorage(this.httpClient, {
        request: {
          engagementId,
        },
      }),
    );
  }

  async restoreEngagementFromArchive(engagementId: string) {
    const response = await engagementRestoreFromArchiveEngagement(
      this.httpClient,
      {
        request: {
          engagementId,
          engagementState: EngagementState.EngagementSignedOff,
        },
      },
    );
    return await this.aptContext.getAPTResultOrThrow(response);
  }

  async getWhoAmI(
    engagementId: string,
    memberFirmId: number | undefined,
  ): Promise<EngagementTeamMember> {
    try {
      const res = await this.aptContext.fetchWhoAmI(engagementId, memberFirmId);
      return await this.aptContext.getAPTResultOrThrow(res, "toast", false);
    } catch (error: unknown) {
      // if we get here we mostlikely dont have access
      return Promise.reject(error);
    }
  }

  async getEngagementTeam(engagementId: string): Promise<EngagementTeam> {
    const res = await this.aptContext.fetchTeamById(engagementId);
    return await this.aptContext.getAPTResultOrThrow(res);
  }

  async isUserLastActiveTeamMember(engagementId: string): Promise<boolean> {
    return await this.aptContext.getAPTResultOrThrow(
      await teamIsUserLastActiveTeamMember(this.httpClient, {
        request: {
          engagementId,
        },
      }),
    );
  }

  async getEngagementTitle(
    engagementId: string,
    engagementMemberFirmId: number | undefined,
  ) {
    const details = await this.aptContext.fetchEngagementDetails(
      engagementId,
      engagementMemberFirmId,
    );

    const result = await this.aptContext.getAPTResultOrThrow(
      details,
      "dialog",
      true,
    );

    return result;
  }

  async getIsRollForwardEnabled(
    engagementId: string,
    libraryVersion: number,
    engagementMemberFirmId: number,
  ): Promise<boolean> {
    try {
      return await this.aptContext.getAPTResultOrThrow(
        await engagementIsRollForwardEngagement(this.httpClient, {
          request: {
            engagementId,
            libraryVersion,
            memberFirmIdOverride: engagementMemberFirmId,
          },
        }),
        "toast",
        false,
      );
    } catch (error: unknown) {
      return Promise.reject(error);
    }
  }

  private getWorkspaceCopyCloneAndRollForwardContent() {
    return import(
      "src/app/engagement/engagement-details/engagement-details.component"
    );
  }

  private getWorkspaceNewContent() {
    return import(
      "src/app/engagement/engagement-details/engagement-details.component"
    );
  }

  public async showAddWorkspace(
    engagementOption: EngagementOption,
    props: ICopyCloneAndRollforwardModalProps | undefined = undefined,
    ignoreExistingSnappedWindows = false,
    inlineStartSnapElement: HTMLElement | undefined = undefined,
  ) {
    if (!props) {
      props = { engagementOption };
    } else {
      props.engagementOption = engagementOption;
    }

    this.isWorkSpacePropertiesOpen.set(true);

    const [res, dialogRef] = await this.windowDialogService.showWithRef(
      this.getWorkspaceNewContent,
      props,
      {
        canChangeSnap: false,
        canClose: true,
        canDrag: false,
        canMaximize: false,
        canMinimize: false,
        canOpenInNewWindow: false,
        canResizeX: "end",
        canResizeY: false,
        modal: true,
        snapDirection: "left",
        snapSize: "420px",
        ignoreExistingSnappedWindows: false,
        inlineStartSnapElement: undefined,
        heightVariant: "withinShellHeader",
      },
      undefined,
      this.childDialogRefs,
    );

    this.isViewingCloneCopyRollforward.set(true);

    dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.engagementDetailsRef = undefined;
        this.isWorkSpacePropertiesOpen.set(false);
        this.isViewingCloneCopyRollforward.set(false);
      });

    this.isWorkSpacePropertiesOpen.set(false);

    return res;
  }

  async openWorkspaceList(animateIn: boolean = true): Promise<boolean> {
    // For non-workspace routes, we want to navigate to the workspace list
    // Otherwise, it'll open as a modal on top of the current route.
    if (!this.router.routerState.snapshot.url.startsWith("/ws")) {
      await this.router.navigateByUrl("/ws");
    } else if (!this.isWorkspaceListOpen()) {
      this.closeNavSide();
      this.isWorkspaceListOpen.set(true);
      const workspaceListModalConfig: ShowWindowDialogOptions =
        this.workspaceListModalConfig();
      workspaceListModalConfig.animateIn = animateIn;
      const [promise, dialogRef] = await this.windowDialogService.showWithRef(
        this.getWorkspaceListComponent,
        {},
        workspaceListModalConfig,
        this.workspaceListDialogRef,
        this.childDialogRefs,
      );

      this.workspaceListDialogRef = dialogRef;

      await promise;
      this.workspaceListDialogRef = undefined;
      this.isWorkspaceListOpen.set(false);
    } else {
      await this.closeWorkspaceList();
    }

    // Always prevent event propagation
    return false;
  }

  async deleteEngagementSnapshot(
    engagementId: string,
  ): Promise<CommandResult | undefined> {
    invariant(this.aptContext.isOfflineWebMode);

    const confirmDelete =
      await this.deleteEngagementSnapshotConfirmationModal();
    if (!confirmDelete) {
      return;
    }

    return await this.aptContext.getAPTResultOrThrow(
      await engagementDeleteEngagement(this.httpClient, {
        request: { engagementId },
      }),
    );
  }

  private async deleteEngagementSnapshotConfirmationModal(): Promise<boolean> {
    const confirmRefreshConfig: StandardConfirmationDialogRequest = {
      title: $localize`Warning`,
      message: $localize`Deleting this engagement snapshot will permanently remove the engagement and it cannot be restored. Continue?`,
      variant: "delete",
    };

    return (await this.modalDialogService.showConfirmationDialog(
      confirmRefreshConfig,
    ))!;
  }

  @precacheComponent("onIdle")
  async getWorkspaceListComponent() {
    return import("src/app/workspace/workspace-list/workspace-list.component");
  }

  async checkForWarningsAndConflicts(
    engagementId: string,
    engagementOption: EngagementOption,
    engagementMemberFirmId: number,
  ): Promise<boolean> {
    try {
      const res = await this.aptContext.getAPTResultOrThrow(
        await engagementGetWarningsAndConflictsCountByEngagementId(
          this.httpClient,
          {
            request: {
              engagementId,
              memberFirmIdOverride: engagementMemberFirmId,
            },
          },
        ),
        "toast",
        false,
      );

      const hasWarnings = res.numWarnings > 0;
      const hasConflicts = res.numConflicts > 0;

      if (!hasConflicts && !hasWarnings) {
        return true;
      }

      let messages: string[] = [];
      const isCopyClone =
        engagementOption === EngagementOption.Copy ||
        engagementOption === EngagementOption.Clone;

      if (hasConflicts && hasWarnings) {
        messages = isCopyClone
          ? [COPY_CLONE_CONFLICTS_AND_WARNINGS_MESSAGE]
          : ROLLFORWARD_CONFLICTS_AND_WARNINGS_MESSAGES;
      } else if (hasConflicts) {
        messages = isCopyClone
          ? [COPY_CLONE_CONFLICTS_MESSAGE]
          : ROLLFORWARD_CONFLICTS_MESSAGES;
      } else if (hasWarnings) {
        messages = isCopyClone
          ? [COPY_CLONE_WARNINGS_MESSAGE]
          : ROLLFORWARD_WARNINGS_MESSAGES;
      }

      const modalConfig = {
        title: $localize`Warning`,
        messages: messages,
        confirmText: $localize`OK`,
        cancelText: $localize`Cancel`,
      } satisfies StandardConfirmationDialogRequest;

      const confirmRes = await this.modalDialogService.showConfirmationDialog(
        modalConfig,
        {
          size: "sm",
        },
      );
      return confirmRes ?? false;
    } catch {
      return false;
    }
  }
}
